import React from 'react';
import cn from 'classnames';
import TextCropper from '@omilia/text-cropper';
import Portal from 'react-portal';
import Highlighter from 'react-highlight-words';
import Tooltip from '@omilia/tooltip';

import './style.scss';

type itemType = {
  id: string;
  name: string;
  category?: string;
  index?: number;
  disabled?: boolean;
};

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  item: itemType;
  isHighlighted?: boolean;
  position?: React.CSSProperties;
  itemWithIndex?: boolean;
  itemIndexOffset?: number;
  itemDisabledReason?: string;
  searchTerm?: string;
  renderOverView?: (item: itemType) => React.ReactNode;
}

const AutoCompleteDefaultItem = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      item,
      isHighlighted,
      position,
      itemWithIndex,
      itemIndexOffset = 0,
      itemDisabledReason,
      searchTerm = '',
      renderOverView,
      ...restProps
    },
    ref,
  ) => {
    const { index = 0, name, disabled } = item;
    const compiledName = itemWithIndex
      ? `${itemIndexOffset + index + 1} - ${name}`
      : name;

    return (
      <div
        ref={ref}
        styleName={cn('item', {
          'is-disabled': disabled,
          'is-active': isHighlighted,
        })}
        {...restProps}
      >
        <TextCropper text={compiledName}>
          <Highlighter
            autoEscape
            highlightStyle={{
              borderBottom: '1px solid #F5F5F5',
            }}
            searchWords={[searchTerm]}
            textToHighlight={compiledName}
          />
        </TextCropper>

        {disabled && itemDisabledReason && (
          <Tooltip content={itemDisabledReason} placement="right">
            <i className="icon-error" data-test="error-icon" />
          </Tooltip>
        )}

        {isHighlighted && renderOverView && (
          <Portal isOpened>
            <div style={position} styleName="extra" data-test="extra">
              {renderOverView(item)}
            </div>
          </Portal>
        )}
      </div>
    );
  },
);

AutoCompleteDefaultItem.displayName = 'AutoCompleteDefaultItem';

export default AutoCompleteDefaultItem;
